/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Video, Image as ImageIcon, Music, Radio, FileQuestion, Play,
  User, Hash, Box, FileEdit, Inbox, QrCode, Maximize2,
  Calendar, HardDrive, Eye, ListTree
} from 'lucide-react';
import defaultLogo from '../assets/logo.png';
import { publicServiceRequestService } from '../services/publicServiceRequestService';
import ErrorScreen from '../components/ErrorScreen';
import StatusBadge from '../components/StatusBadge';
import VideoPlayer from '../components/VideoPlayer';
import AudioPlayer from '../components/AudioPlayer';
import QRCodeDisplay from '../components/QRCode';
import Modal from '../components/Modal';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { formatFileSize } from '../utils/formatFileSize';
import {
  SERVICE_REQUEST_MEDIA_FILE_TYPES,
  SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS
} from '../constants/serviceRequests';
import { SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_GONE = 410;

const MEDIA_TYPE_ICONS = {
  video: Video,
  image: ImageIcon,
  audio: Music,
  session_recording: Radio,
};

const formatDateTime = (date) => {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
};

/**
 * PublicMediaCard - Compact interactive media item card
 */
const PublicMediaCard = ({ item, onOpen }) => {
  const Icon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;
  const [previewFailed, setPreviewFailed] = useState(false);
  const isImage = item.fileType === 'image';
  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const showRealPreview = (isImage || isVideoLike) && !previewFailed;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className="group relative text-left bg-base-100 rounded-xl overflow-hidden border border-base-content/10 hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50"
    >
      {/* Media Stage Preview Thumbnail */}
      <div className="aspect-[16/10] bg-base-200 relative overflow-hidden flex items-center justify-center w-full select-none">
        {showRealPreview ? (
          isImage ? (
            <img
              src={item.fileUrl}
              alt={item.fileName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out"
              onError={() => setPreviewFailed(true)}
            />
          ) : (
            <>
              <video
                src={item.fileUrl}
                muted
                preload="metadata"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out"
                onError={() => setPreviewFailed(true)}
              />
              {/* Floating Center Glassmorphism Play Button */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/15 transition-colors">
                <div className="w-8 h-8 rounded-full bg-primary/95 text-primary-content flex items-center justify-center shadow-md backdrop-blur-xs transform group-hover:scale-110 transition-transform duration-200">
                  <Play size={13} className="fill-current ml-0.5" />
                </div>
              </div>
            </>
          )
        ) : (
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-105 transition-transform">
            <Icon size={20} />
          </div>
        )}

        {/* Top-Left Frosted Badge */}
        <div className="absolute top-2 left-2 pointer-events-none">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider bg-black/65 text-white backdrop-blur-md border border-white/10 shadow-2xs">
            <Icon size={10} />
            {SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType}
          </span>
        </div>

        {/* Hover Action Hint Overlay */}
        <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-base-100/90 text-base-content backdrop-blur-md shadow-2xs">
            <Eye size={10} className="text-primary" />
            <span>Preview</span>
          </span>
        </div>
      </div>

      {/* Card Content Footer */}
      <div className="p-2.5 sm:p-3 flex-1 flex flex-col justify-between space-y-1.5">
        <p className="text-xs font-bold text-base-content line-clamp-1 group-hover:text-primary transition-colors" title={item.fileName}>
          {item.fileName}
        </p>
        <div className="flex items-center justify-between text-[10px] text-base-content/50 font-medium pt-1 border-t border-base-content/5">
          <span className="font-semibold text-base-content/70">{formatFileSize(item.fileSize)}</span>
          <span>{formatRelativeTime(item.createdAt)}</span>
        </div>
      </div>
    </div>
  );
};

PublicMediaCard.propTypes = {
  item: PropTypes.shape({
    fileType: PropTypes.string,
    fileName: PropTypes.string,
    fileSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fileUrl: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

/**
 * PublicMediaViewerModal - In-built player & theater modal
 */
const PublicMediaViewerModal = ({ item, onClose }) => {
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    setPreviewFailed(false);
  }, [item?.fileUrl]);

  if (!item) return null;

  const isVideoLike = item.fileType === 'video' || item.fileType === 'session_recording';
  const isAudio = item.fileType === 'audio';
  const isImage = item.fileType === 'image';
  const TypeIcon = MEDIA_TYPE_ICONS[item.fileType] || FileQuestion;

  return (
    <Modal
      isOpen={!!item}
      onClose={onClose}
      title={item.fileName || 'Media Preview'}
      className="max-w-5xl w-full max-h-[90vh]"
    >
      <div className="flex flex-col md:flex-row gap-5 items-stretch min-h-0">
        {/* Left Column: In-Built Media Stage Viewport */}
        <div className="flex-1 min-w-0 bg-base-200/70 dark:bg-black/50 border border-base-content/10 overflow-hidden shadow-inner flex items-center justify-center relative min-h-[280px] sm:min-h-[360px] md:min-h-[440px] h-[340px] sm:h-[420px] md:h-[500px] max-h-[60vh] md:max-h-[70vh] rounded-2xl">
          {isImage && !previewFailed && (
            <div className="relative group/preview w-full h-full flex items-center justify-center p-3">
              <img
                src={item.fileUrl}
                alt={item.fileName}
                className="max-w-full max-h-full object-contain select-none shadow-md"
                onError={() => setPreviewFailed(true)}
              />
              <a
                href={item.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-3 right-3 px-3 py-1.5 rounded-xl bg-base-100/90 hover:bg-base-100 text-base-content/80 hover:text-primary shadow-lg backdrop-blur-md opacity-0 group-hover/preview:opacity-100 transition-all text-xs font-bold flex items-center gap-1.5"
                title="Open full resolution in new tab"
              >
                <Maximize2 size={13} />
                <span>Full size</span>
              </a>
            </div>
          )}

          {isVideoLike && (
            <div className="w-full h-full min-h-0 flex items-center justify-center">
              <VideoPlayer src={item.fileUrl} />
            </div>
          )}

          {isAudio && (
            <div className="w-full p-6 sm:p-8 max-w-lg">
              <AudioPlayer src={item.fileUrl} fileName={item.fileName} />
            </div>
          )}

          {((!isImage && !isVideoLike && !isAudio) || (isImage && previewFailed)) && (
            <div className="py-12 px-6 flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner">
                <TypeIcon size={32} />
              </div>
              <div>
                <p className="text-sm font-bold text-base-content">{item.fileName}</p>
                <p className="text-xs text-base-content/50 mt-0.5">
                  {previewFailed ? 'Preview could not be loaded' : 'Preview not available in player'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Details Panel */}
        <div className="md:w-72 lg:w-80 flex-shrink-0 flex flex-col justify-between gap-4 min-w-0">
          <div className="space-y-4">
            {/* Type Badge & File Size */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                  <TypeIcon size={12} />
                  <span>{SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[item.fileType] || item.fileType || 'Media'}</span>
                </span>
                <span className="text-xs font-bold text-base-content/60">
                  {formatFileSize(item.fileSize)}
                </span>
              </div>
              <p className="text-sm font-bold text-base-content break-words line-clamp-2" title={item.fileName}>
                {item.fileName}
              </p>
            </div>

            {/* Metadata Information Card */}
            <div className="space-y-2 pt-3 border-t border-base-content/10 text-xs">
              <p className="text-[10px] font-black uppercase tracking-wider text-base-content/40">File Information</p>
              <div className="flex items-center justify-between py-1.5 border-b border-base-content/5">
                <span className="text-base-content/50 font-medium flex items-center gap-1.5">
                  <HardDrive size={13} className="text-base-content/40" /> Size
                </span>
                <span className="font-bold text-base-content">{formatFileSize(item.fileSize)}</span>
              </div>

              <div className="flex items-center justify-between py-1.5 border-b border-base-content/5">
                <span className="text-base-content/50 font-medium flex items-center gap-1.5">
                  <Calendar size={13} className="text-base-content/40" /> Date Added
                </span>
                <span className="font-bold text-base-content">
                  {formatDateTime(item.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

PublicMediaViewerModal.propTypes = {
  item: PropTypes.shape({
    fileType: PropTypes.string,
    fileName: PropTypes.string,
    fileSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fileUrl: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }),
  onClose: PropTypes.func.isRequired,
};

/**
 * PublicServiceRequest - Standalone Customer/Partner Service Request Page
 */
const PublicServiceRequest = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [selectedMediaItem, setSelectedMediaItem] = useState(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [showQrModal, setShowQrModal] = useState(false);
  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    setPayload(null);

    const fetchServiceRequest = async () => {
      try {
        const { data } = await publicServiceRequestService.getByCode(code);
        if (ignore) return;
        setPayload(data);
      } catch (err) {
        if (ignore) return;
        const status = err.response?.status;
        if (status === HTTP_STATUS_NOT_FOUND) {
          setError({ title: 'Public Link Not Found', message: 'The link you are trying to access does not exist.', icon: 'notFound' });
        } else if (status === HTTP_STATUS_GONE) {
          const message = err.response?.data?.message || '';
          if (message.toLowerCase().includes('revoked')) {
            setError({ title: 'Public Link Unavailable', message: 'This link has been revoked and is no longer available.', icon: 'forbidden' });
          } else {
            setError({ title: 'Public Link Expired', message: 'This link has expired. Please request a new one.', icon: 'expired' });
          }
        } else {
          setError({ title: 'Something Went Wrong', message: `Unable to load this ${SERVICE_REQUEST_LABEL.singularLower} right now. Please try again later.`, icon: 'error' });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchServiceRequest();

    return () => {
      ignore = true;
    };
  }, [code]);

  // Update page title and favicon when payload arrives
  useEffect(() => {
    if (!payload) return;
    const { serviceRequest, subAccount } = payload;
    const subName = subAccount?.name || SERVICE_REQUEST_LABEL.singular;
    document.title = `${serviceRequest.requestNumber} | ${subName}`;

    if (subAccount?.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = subAccount.faviconUrl;
    }
  }, [payload]);

  const mediaItems = useMemo(() => payload?.mediaItems || [], [payload?.mediaItems]);
  const serviceRequest = payload?.serviceRequest || {};
  const subAccount = payload?.subAccount || null;
  const mediaTotal = payload?.mediaTotal ?? mediaItems.length;
  const mediaTruncated = payload?.mediaTruncated ?? false;

  const getMediaCountByType = (type) => {
    if (type === 'all') {
      return mediaItems.length;
    }
    return mediaItems.filter((m) => m.fileType === type).length;
  };

  const filteredMedia = useMemo(() => {
    if (mediaFilter === 'all') return mediaItems;
    return mediaItems.filter((i) => i.fileType === mediaFilter);
  }, [mediaItems, mediaFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <ErrorScreen title={error.title} message={error.message} icon={error.icon} />;
  }

  const logoSrc = subAccount?.logoUrl || defaultLogo;
  const logoAlt = subAccount?.name || 'Send2Me Logo';

  return (
    <div
      data-theme={subAccount?.theme || 'light'}
      className="min-h-screen bg-gradient-to-br from-base-200/80 via-base-100 to-base-200/80 text-base-content flex flex-col font-sans transition-colors duration-200 selection:bg-primary/20 selection:text-primary"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Top Header Bar with Sub-Account Branding                           */}
      {/* ------------------------------------------------------------------ */}
      <header className="sticky top-0 z-30 bg-base-100/85 backdrop-blur-xl border-b border-base-content/10 shadow-xs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Sub-Account Logo & Name Branding */}
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={logoSrc}
              alt={logoAlt}
              className="h-9 max-h-9 w-auto max-w-[150px] sm:max-w-[220px] object-contain rounded-lg"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
                e.currentTarget.onerror = null;
              }}
            />
            {subAccount?.name && (
              <p className="text-base font-bold text-base-content truncate tracking-tight">
                {subAccount.name}
              </p>
            )}
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {subAccount?.showQrCode !== false && (
              <button
                type="button"
                onClick={() => setShowQrModal(true)}
                className="px-3 py-1.5 rounded-xl bg-base-200/80 hover:bg-base-200 text-base-content/75 hover:text-base-content border border-base-content/10 text-xs font-bold transition flex items-center gap-1.5 shadow-2xs hover:shadow-xs active:scale-95"
                title="Scan QR code on mobile"
              >
                <QrCode size={14} className="text-primary" />
                <span className="hidden sm:inline">Mobile QR</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Main Container Area                                                */}
      {/* ------------------------------------------------------------------ */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-9 space-y-6 sm:space-y-8">
        {/* ================================================================ */}
        {/* SECTION 1: Service Request Details Card (Executive Overview)     */}
        {/* ================================================================ */}
        <div className="relative bg-base-100 rounded-3xl p-6 sm:p-8 border border-base-content/10 shadow-sm overflow-hidden">
          {/* Ambient Glow Accent in corner */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />

          {/* Top Row: Status, Type Pill, & Creation Timestamps */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 pb-5 border-b border-base-content/10">
            <div className="flex items-center gap-2.5 flex-wrap">
              <StatusBadge status={serviceRequest.status} />
              {(serviceRequest.typeLabel || serviceRequest.type) && (
                <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20 shadow-2xs">
                  {serviceRequest.typeLabel || serviceRequest.type}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-xs text-base-content/60 font-medium">
              <Calendar size={13} className="text-base-content/40" />
              <span>{formatDateTime(serviceRequest.createdAt)}</span>
            </div>
          </div>

          {/* Service Request Title & Number */}
          <div className="pt-5 pb-6">
            <p className="text-[11px] font-black uppercase tracking-widest text-base-content/40 mb-1">
              {serviceRequest.requestNumberLabel || SERVICE_REQUEST_LABEL.singular}
            </p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-base-content tracking-tight">
              {serviceRequest.requestNumber}
            </h1>
          </div>

          {/* Details Tiles: Customer, hierarchy levels, Asset ID, Equipment ID */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
            {/* Customer Tile */}
            <div className="bg-base-200/50 hover:bg-base-200/75 transition-colors p-4 rounded-2xl border border-base-content/5 flex items-start gap-3.5 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center flex-shrink-0 shadow-inner">
                <User size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                  {serviceRequest.customerNameLabel || 'Customer Name'}
                </p>
                <p className="text-sm font-bold text-base-content truncate" title={serviceRequest.customerName || '—'}>
                  {serviceRequest.customerName || '—'}
                </p>
              </div>
            </div>

            {/* Type option hierarchy — one tile per selected level */}
            {(serviceRequest.hierarchySelections || []).map((sel) => (
              <div key={`${sel.depth}-${sel.nodeName}`} className="bg-base-200/50 hover:bg-base-200/75 transition-colors p-4 rounded-2xl border border-base-content/5 flex items-start gap-3.5 shadow-2xs">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-500 flex items-center justify-center flex-shrink-0 shadow-inner">
                  <ListTree size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                    {sel.levelName}
                  </p>
                  <p className="text-sm font-bold text-base-content truncate" title={sel.nodeName}>
                    {sel.nodeName}
                  </p>
                </div>
              </div>
            ))}

            {/* Asset Identifier Tile */}
            <div className="bg-base-200/50 hover:bg-base-200/75 transition-colors p-4 rounded-2xl border border-base-content/5 flex items-start gap-3.5 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center flex-shrink-0 shadow-inner">
                <Hash size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                  {serviceRequest.assetIdentifierLabel || 'Asset Identifier'}
                </p>
                <p className="text-sm font-bold text-base-content truncate" title={serviceRequest.assetIdentifier || '—'}>
                  {serviceRequest.assetIdentifier || '—'}
                </p>
              </div>
            </div>

            {/* Equipment Identifier Tile */}
            <div className="bg-base-200/50 hover:bg-base-200/75 transition-colors p-4 rounded-2xl border border-base-content/5 flex items-start gap-3.5 shadow-2xs">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center flex-shrink-0 shadow-inner">
                <Box size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/40 mb-0.5">
                  {serviceRequest.equipmentIdentifierLabel || 'Equipment Identifier'}
                </p>
                <p className="text-sm font-bold text-base-content truncate" title={serviceRequest.equipmentIdentifier || '—'}>
                  {serviceRequest.equipmentIdentifier || '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Details Notes Block */}
          {serviceRequest.additionalDetails && (
            <div className="mt-5 pt-5 border-t border-base-content/10">
              <div className="flex items-center gap-2 mb-2.5">
                <FileEdit size={14} className="text-primary" />
                <p className="text-[10px] font-black uppercase tracking-widest text-base-content/50">
                  Additional Details
                </p>
              </div>
              <div className="bg-base-200/60 p-4 sm:p-5 rounded-2xl border-l-4 border-l-primary border-y border-r border-base-content/5 shadow-2xs">
                <p className="text-sm text-base-content leading-relaxed whitespace-pre-wrap break-words font-medium">
                  {serviceRequest.additionalDetails}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* SECTION 2: Media Attachments (Listing with Modal Player on Click)*/}
        {/* ================================================================ */}
        <div className="bg-base-100 rounded-3xl p-6 sm:p-8 border border-base-content/10 shadow-sm space-y-6">
          {/* Section Header & Filter Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-base-content/10">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-black text-base-content tracking-tight">
                Media Attachments
              </h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                {mediaTotal}
              </span>
              {mediaTruncated && (
                <span className="text-xs font-semibold text-warning" title={`Showing first ${mediaItems.length} of ${mediaTotal} items`}>
                  Showing first {mediaItems.length} of {mediaTotal}
                </span>
              )}
            </div>

            {/* Filter Pills */}
            {mediaItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {['all', ...SERVICE_REQUEST_MEDIA_FILE_TYPES].map((typeKey) => {
                  const count = getMediaCountByType(typeKey);
                  const isSelected = mediaFilter === typeKey;
                  const label = typeKey === 'all' ? 'All' : SERVICE_REQUEST_MEDIA_FILE_TYPE_LABELS[typeKey] || typeKey;

                  return (
                    <button
                      key={typeKey}
                      type="button"
                      onClick={() => setMediaFilter(typeKey)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${isSelected
                        ? 'bg-primary text-primary-content shadow-sm'
                        : 'bg-base-200/60 hover:bg-base-200 text-base-content/60 border border-base-content/5'
                        }`}
                    >
                      <span>{label}</span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? 'bg-primary-content/20 text-primary-content' : 'bg-base-content/10 text-base-content/60'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Media Items Grid or Empty State */}
          {mediaItems.length === 0 ? (
            <div className="py-14 flex flex-col items-center justify-center text-center space-y-3">
              <div className="w-16 h-16 rounded-3xl bg-base-200/80 flex items-center justify-center text-base-content/30 shadow-inner">
                <Inbox size={28} />
              </div>
              <div>
                <p className="text-sm font-bold text-base-content">No media attachments found</p>
                <p className="text-xs text-base-content/50 mt-0.5">
                  No video, audio, or images were recorded for this {SERVICE_REQUEST_LABEL.singularLower}.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {filteredMedia.map((item) => (
                <PublicMediaCard
                  key={`${item.fileName}-${item.fileUrl}`}
                  item={item}
                  onOpen={(clickedItem) => setSelectedMediaItem(clickedItem)}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ------------------------------------------------------------------ */}
      {/* Media Viewer & In-Built Player Modal                               */}
      {/* ------------------------------------------------------------------ */}
      <PublicMediaViewerModal
        item={selectedMediaItem}
        onClose={() => setSelectedMediaItem(null)}
      />

      {/* ------------------------------------------------------------------ */}
      {/* QR Code Modal Dialog                                               */}
      {/* ------------------------------------------------------------------ */}
      <Modal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        title="Mobile QR Code"
        className="max-w-sm w-full"
      >
        <div className="py-3 flex flex-col items-center text-center space-y-4">
          <QRCodeDisplay value={window.location.href} size={180} />
          <p className="text-xs text-base-content/60 max-w-xs">
            Scan this code with a mobile camera to view this {SERVICE_REQUEST_LABEL.singularLower} on your phone.
          </p>
          <button
            type="button"
            onClick={() => setShowQrModal(false)}
            className="w-full py-2.5 rounded-xl bg-base-200 hover:bg-base-300 text-base-content font-bold text-xs transition"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PublicServiceRequest;
